import { createStore, filterNil, select, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { getCurrentAuthData } from '@tmx/auth';

import { UserPreferences } from '../models/user-preferences';
import {
  DEFAULT_ASSETS_COLUMN_ORDER,
  DEFAULT_ASSETS_TABLE_RESIZABLE_COLUMN_WIDTHS,
  DEFAULT_CONFIG_PROFILES_COLUMN_ORDER,
  DEFAULT_CUSTOM_PLACES_COLUMN_ORDER,
  DEFAULT_DRIVER_GROUPS_COLUMN_ORDER,
  DEFAULT_DRIVERS_COLUMN_ORDER,
  DEFAULT_MAP_SETS_COLUMN_ORDER,
  DEFAULT_ROUTE_MODIFIERS_COLUMN_ORDER,
  DEFAULT_ROUTING_PROFILES_COLUMN_ORDER,
  DEFAULT_SPEED_COACHING_PROFILES_COLUMN_ORDER,
  DEFAULT_VEHICLE_GROUPS_COLUMN_ORDER,
  DEFAULT_VEHICLES_COLUMN_ORDER,
} from '../models/user-preferences-defaults';

interface UserPreferencesProps {
  preferencesMap: Record<string, Partial<UserPreferences> | undefined>;
}

const userPreferencesStore = createStore(
  { name: 'user-preferences' },
  withProps<UserPreferencesProps>({
    preferencesMap: {},
  })
);

export const persist = persistState(userPreferencesStore, {
  key: 'user-preferences',
  storage: localStorageStrategy,
});

function setDefaultPreferencesForCurrentUser() {
  const { username } = getCurrentAuthData().user;

  const defaultPreferences: UserPreferences = {
    assetsTableColumnOrderPreference: DEFAULT_ASSETS_COLUMN_ORDER,
    assetsTableDisplayCheckboxes: null,
    assetsTableResizableColumnWidths:
      DEFAULT_ASSETS_TABLE_RESIZABLE_COLUMN_WIDTHS,
    vehiclesTableColumnOrderPreference: DEFAULT_VEHICLES_COLUMN_ORDER,
    vehiclesTableDisplayCheckboxes: null,
    vehicleGroupsTableColumnOrderPreference:
      DEFAULT_VEHICLE_GROUPS_COLUMN_ORDER,
    vehicleGroupsTableDisplayCheckboxes: null,
    routingProfilesTableColumnOrderPreference:
      DEFAULT_ROUTING_PROFILES_COLUMN_ORDER,
    routingProfilesTableDisplayCheckboxes: null,
    configurationProfilesTableColumnOrderPreference:
      DEFAULT_CONFIG_PROFILES_COLUMN_ORDER,
    configurationProfilesTableDisplayCheckboxes: null,
    mapSetsTableColumnOrderPreference: DEFAULT_MAP_SETS_COLUMN_ORDER,
    mapSetsTableDisplayCheckboxes: null,
    routeModifiersTableColumnOrderPreference:
      DEFAULT_ROUTE_MODIFIERS_COLUMN_ORDER,
    routeModifiersTableDisplayCheckboxes: null,
    customPlacesTableColumnOrderPreference: DEFAULT_CUSTOM_PLACES_COLUMN_ORDER,
    customPlacesTableDisplayCheckboxes: null,
    driversTableColumnOrderPreference: DEFAULT_DRIVERS_COLUMN_ORDER,
    driversTableDisplayCheckboxes: null,
    driverGroupsTableColumnOrderPreference: DEFAULT_DRIVER_GROUPS_COLUMN_ORDER,
    driverGroupsTableDisplayCheckboxes: null,
    speedCoachingProfilesTableColumnOrderPreference:
      DEFAULT_SPEED_COACHING_PROFILES_COLUMN_ORDER,
    speedCoachingProfilesTableDisplayCheckboxes: null,
  };

  const preferencesMap = userPreferencesStore.getValue().preferencesMap;

  preferencesMap[username] = defaultPreferences;

  userPreferencesStore.update((state) => ({
    ...state,
    preferencesMap,
  }));

  return defaultPreferences;
}

/** Snapshots */

export const userPreferencesSnapshot = () => {
  const { username } = getCurrentAuthData().user;
  const preferencesMap = userPreferencesStore.getValue().preferencesMap;

  const userPreferences = preferencesMap[username];

  const preferences: UserPreferences = userPreferences
    ? {
        assetsTableColumnOrderPreference:
          userPreferences.assetsTableColumnOrderPreference ??
          DEFAULT_ASSETS_COLUMN_ORDER,
        assetsTableDisplayCheckboxes:
          userPreferences.assetsTableDisplayCheckboxes ?? null,
        assetsTableResizableColumnWidths:
          userPreferences.assetsTableResizableColumnWidths ??
          DEFAULT_ASSETS_TABLE_RESIZABLE_COLUMN_WIDTHS,
        vehiclesTableColumnOrderPreference:
          userPreferences.vehiclesTableColumnOrderPreference ??
          DEFAULT_VEHICLES_COLUMN_ORDER,
        vehiclesTableDisplayCheckboxes:
          userPreferences.vehiclesTableDisplayCheckboxes ?? null,
        vehicleGroupsTableColumnOrderPreference:
          userPreferences.vehicleGroupsTableColumnOrderPreference ??
          DEFAULT_VEHICLE_GROUPS_COLUMN_ORDER,
        vehicleGroupsTableDisplayCheckboxes:
          userPreferences.vehicleGroupsTableDisplayCheckboxes ?? null,
        routingProfilesTableColumnOrderPreference:
          userPreferences.routingProfilesTableColumnOrderPreference ??
          DEFAULT_ROUTING_PROFILES_COLUMN_ORDER,
        routingProfilesTableDisplayCheckboxes:
          userPreferences.routingProfilesTableDisplayCheckboxes ?? null,
        configurationProfilesTableColumnOrderPreference:
          userPreferences.configurationProfilesTableColumnOrderPreference ??
          DEFAULT_CONFIG_PROFILES_COLUMN_ORDER,
        configurationProfilesTableDisplayCheckboxes:
          userPreferences.configurationProfilesTableDisplayCheckboxes ?? null,
        mapSetsTableColumnOrderPreference:
          userPreferences.mapSetsTableColumnOrderPreference ??
          DEFAULT_MAP_SETS_COLUMN_ORDER,
        mapSetsTableDisplayCheckboxes:
          userPreferences.mapSetsTableDisplayCheckboxes ?? null,
        routeModifiersTableColumnOrderPreference:
          userPreferences.routeModifiersTableColumnOrderPreference ??
          DEFAULT_ROUTE_MODIFIERS_COLUMN_ORDER,
        routeModifiersTableDisplayCheckboxes:
          userPreferences.routeModifiersTableDisplayCheckboxes ?? null,
        customPlacesTableColumnOrderPreference:
          userPreferences.customPlacesTableColumnOrderPreference ??
          DEFAULT_CUSTOM_PLACES_COLUMN_ORDER,
        customPlacesTableDisplayCheckboxes:
          userPreferences.customPlacesTableDisplayCheckboxes ?? null,
        driversTableColumnOrderPreference:
          userPreferences.driversTableColumnOrderPreference ??
          DEFAULT_DRIVERS_COLUMN_ORDER,
        driversTableDisplayCheckboxes:
          userPreferences.driversTableDisplayCheckboxes ?? null,
        driverGroupsTableColumnOrderPreference:
          userPreferences.driverGroupsTableColumnOrderPreference ??
          DEFAULT_DRIVER_GROUPS_COLUMN_ORDER,
        driverGroupsTableDisplayCheckboxes:
          userPreferences.driverGroupsTableDisplayCheckboxes ?? null,
        speedCoachingProfilesTableColumnOrderPreference:
          userPreferences.speedCoachingProfilesTableColumnOrderPreference ??
          DEFAULT_SPEED_COACHING_PROFILES_COLUMN_ORDER,
        speedCoachingProfilesTableDisplayCheckboxes:
          userPreferences.speedCoachingProfilesTableDisplayCheckboxes ?? null,
      }
    : setDefaultPreferencesForCurrentUser();

  return preferences;
};

/** Exposed Variables and Observables */

export const userPreferences$ = userPreferencesStore.pipe(
  select(({ preferencesMap }) => {
    const { username } = getCurrentAuthData().user;

    return preferencesMap[username];
  }),
  filterNil()
);

/** Updater Functions */
export function updateUserPreferences(preferences: Partial<UserPreferences>) {
  const { username } = getCurrentAuthData().user;
  const preferencesMap = userPreferencesStore.getValue().preferencesMap;

  const userPreferences =
    typeof preferencesMap[username] === 'undefined'
      ? setDefaultPreferencesForCurrentUser()
      : preferencesMap[username];

  const newPreferences = {
    ...userPreferences,
    ...preferences,
  };

  preferencesMap[username] = newPreferences;

  userPreferencesStore.update((state) => ({
    ...state,
    preferencesMap,
  }));
}
